import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { useHistory, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Container, Row, Col, Card, Button, Badge, Alert, Table, InputGroup, Form, Modal, Nav } from 'react-bootstrap';
import { generatePlainTextExcerpt } from '../../../utils/excerptUtils';
import { calculateSeoScore } from '../../../utils/seoUtils';

const POSTS_PER_PAGE = 10;

// Social media testing tools
const SOCIAL_TOOLS = [
  {
    name: 'Facebook Sharing Debugger',
    url: 'https://developers.facebook.com/tools/debug/',
    description: 'Test how your posts appear when shared on Facebook. Scrape new info and preview Open Graph tags.'
  },
  {
    name: 'Twitter Card Validator',
    url: 'https://cards-dev.twitter.com/validator',
    description: 'Preview how your posts appear as Twitter Cards. Validate meta tags and image rendering.'
  },
  {
    name: 'LinkedIn Post Inspector',
    url: 'https://www.linkedin.com/post-inspector/',
    description: 'Check how your content appears when shared on LinkedIn. Refresh cached previews.'
  },
  {
    name: 'Google Rich Results Test',
    url: 'https://search.google.com/test/rich-results',
    description: 'Test structured data and see which rich results can be generated for your page.'
  },
  {
    name: 'OpenGraph.xyz',
    url: 'https://www.opengraph.xyz/',
    description: 'Preview Open Graph tags across multiple platforms at once.'
  }
];

const AiNotesAdmin = ({ auth }) => {
  const history = useHistory();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [postToDelete, setPostToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [activeTab, setActiveTab] = useState('posts');

  // Check admin authorization
  useEffect(() => {
    // auth === false means user is not authenticated (logged out)
    // auth === null/undefined means auth is still loading
    // Wait for auth to finish loading
    if (auth === null || auth === undefined) {
      // Still loading auth - wait
      return;
    }
    
    // If auth is false, user is logged out - redirect to homepage
    if (auth === false) {
      history.push('/');
      return;
    }
    
    // Now check if user is admin
    if (!auth.id || auth.role !== 'admin') {
      history.push('/');
    }
  }, [auth, history]);

  useEffect(() => {
    if (auth && auth.role === 'admin') {
      fetchPosts();
    }
  }, [auth]);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch('/api/posts/admin/all', {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setPosts(data.posts || []);
      } else {
        setError('Failed to load posts');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (post) => {
    setPostToDelete(post);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!postToDelete) return;

    setDeleting(true);
    try {
      const response = await fetch(`/api/posts/${postToDelete.id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        setShowDeleteModal(false);
        setPostToDelete(null);
        fetchPosts();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to delete post');
        setShowDeleteModal(false);
        setPostToDelete(null);
      }
    } catch (err) {
      setError('Error deleting post: ' + err.message);
      setShowDeleteModal(false);
      setPostToDelete(null);
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setPostToDelete(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    
    // Dates now come from backend as UTC ISO strings (e.g., "2025-12-31T03:43:00.000Z")
    // Parse and convert to CT timezone
    const date = new Date(dateString);
    
    // Verify the date is valid
    if (isNaN(date.getTime())) {
      console.error('Invalid date:', dateString);
      return '—';
    }
    
    // Format in CT timezone (America/Chicago)
    // toLocaleString converts from UTC to CT automatically
    const formatted = date.toLocaleString('en-US', {
      timeZone: 'America/Chicago',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
    
    return formatted + ' CT';
  };

  const truncateText = (text, maxLength = 80) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // Filter posts by search term
  const filteredPosts = posts.filter(post => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      post.title?.toLowerCase().includes(search) ||
      post.content?.toLowerCase().includes(search) ||
      post.status?.toLowerCase().includes(search)
    );
  });

  // Calculate pagination
  const totalPages = Math.ceil(filteredPosts.length / POSTS_PER_PAGE);
  const startIndex = (currentPage - 1) * POSTS_PER_PAGE;
  const endIndex = startIndex + POSTS_PER_PAGE;
  const paginatedPosts = filteredPosts.slice(startIndex, endIndex);

  // Don't render if auth is still loading (null/undefined)
  // auth === false means logged out (redirect handled in useEffect)
  if (auth === null || auth === undefined) {
    return (
      <div style={{
        padding: '120px 60px 60px 60px',
        minHeight: 'calc(100vh - 200px)'
      }}>
        <Container>
          <div className="text-center">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3">Checking permissions...</p>
          </div>
        </Container>
      </div>
    );
  }
  
  // If auth is false (logged out) or user is not admin, return null (redirect handled in useEffect)
  if (auth === false || !auth.id || auth.role !== 'admin') {
    return null;
  }

  if (loading) {
    return (
      <div style={{
        padding: '120px 60px 60px 60px',
        minHeight: 'calc(100vh - 200px)'
      }}>
        <Container>
          <div className="text-center">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3">Loading posts...</p>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div style={{
      padding: '120px 60px 60px 60px',
      minHeight: 'calc(100vh - 200px)'
    }}>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <title>AI Notes Admin - Elliot Koss</title>
      </Helmet>
      <Container fluid>
        <Row>
          <Col>
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <h2 style={{
                  fontWeight: '300',
                  color: '#2d3748',
                  marginBottom: '0.5rem'
                }}>Post Notes Admin</h2>
                <p className="text-muted mb-0">Manage your Post Notes posts</p>
              </div>
              {activeTab === 'posts' && (
                <Link to="/admin/ai-notes/new">
                  <Button
                    variant="primary"
                    style={{
                      backgroundColor: '#667eea',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '10px 24px',
                      fontWeight: '500'
                    }}
                  >
                    + New Post
                  </Button>
                </Link>
              )}
            </div>

            {/* Tabs */}
            <Nav variant="tabs" className="mb-4" style={{ borderBottom: '2px solid #e2e8f0' }}>
              <Nav.Item>
                <Nav.Link
                  active={activeTab === 'posts'}
                  onClick={() => setActiveTab('posts')}
                  style={{
                    color: activeTab === 'posts' ? '#667eea' : '#718096',
                    fontWeight: activeTab === 'posts' ? '600' : '400',
                    border: 'none',
                    borderBottom: activeTab === 'posts' ? '2px solid #667eea' : '2px solid transparent',
                    marginBottom: '-2px',
                    cursor: 'pointer'
                  }}
                >
                  Posts
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link
                  active={activeTab === 'tools'}
                  onClick={() => setActiveTab('tools')}
                  style={{
                    color: activeTab === 'tools' ? '#667eea' : '#718096',
                    fontWeight: activeTab === 'tools' ? '600' : '400',
                    border: 'none',
                    borderBottom: activeTab === 'tools' ? '2px solid #667eea' : '2px solid transparent',
                    marginBottom: '-2px',
                    cursor: 'pointer'
                  }}
                >
                  Tools
                </Nav.Link>
              </Nav.Item>
            </Nav>

            {error && (
              <Alert variant="danger" dismissible onClose={() => setError(null)} className="mb-4">
                {error}
              </Alert>
            )}

            {/* Posts Tab Content */}
            {activeTab === 'posts' && (
              <>
            {/* Search */}
            <Card className="mb-4" style={{
              border: 'none',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
            }}>
              <Card.Body>
                <InputGroup>
                  <Form.Control
                    type="text"
                    placeholder="Search posts by title, content, or status..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setCurrentPage(1); // Reset to first page on search
                    }}
                    style={{
                      borderRadius: '8px',
                      border: '1px solid #e2e8f0',
                      padding: '10px 16px'
                    }}
                  />
                </InputGroup>
              </Card.Body>
            </Card>

            {/* Posts Table */}
            <Card style={{
              border: 'none',
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
              overflow: 'hidden'
            }}>
              <Card.Body className="p-0">
                {paginatedPosts.length === 0 ? (
                  <div className="text-center py-5">
                    <p className="text-muted mb-4">
                      {searchTerm ? 'No posts match your search' : 'No posts yet. Create your first post!'}
                    </p>
                    {!searchTerm && (
                      <Link to="/admin/ai-notes/new">
                        <Button variant="primary" style={{
                          backgroundColor: '#667eea',
                          border: 'none',
                          borderRadius: '8px'
                        }}>
                          Create First Post
                        </Button>
                      </Link>
                    )}
                  </div>
                ) : (
                  <>
                    <Table responsive hover className="mb-0">
                      <thead style={{
                        backgroundColor: '#f7fafc',
                        borderBottom: '2px solid #e2e8f0'
                      }}>
                        <tr>
                          <th style={{ padding: '16px', fontWeight: '600', color: '#4a5568', width: '60px' }}>Image</th>
                          <th style={{ padding: '16px', fontWeight: '600', color: '#4a5568' }}>Title</th>
                          <th style={{ padding: '16px', fontWeight: '600', color: '#4a5568' }}>Status</th>
                          <th style={{ padding: '16px', fontWeight: '600', color: '#4a5568', width: '90px' }}>SEO</th>
                          <th style={{ padding: '16px', fontWeight: '600', color: '#4a5568' }}>Posted</th>
                          <th style={{ padding: '16px', fontWeight: '600', color: '#4a5568' }}>Scheduled</th>
                          <th style={{ padding: '16px', fontWeight: '600', color: '#4a5568' }}>Updated</th>
                          <th style={{ padding: '16px', fontWeight: '600', color: '#4a5568', textAlign: 'right' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedPosts.map((post) => (
                          <tr key={post.id} style={{
                            borderBottom: '1px solid #e2e8f0'
                          }}>
                            <td style={{ padding: '16px', verticalAlign: 'middle', width: '60px' }}>
                              <div
                                onClick={() => {
                                  if (post.slug && post.status === 'published') {
                                    history.push(`/ai-notes/${post.slug}`);
                                  }
                                }}
                                style={{
                                  cursor: (post.slug && post.status === 'published') ? 'pointer' : 'default'
                                }}
                              >
                                {post.image && (post.image.webp_url || post.image.s3_url) ? (
                                  <img
                                    src={post.image.webp_url || post.image.s3_url}
                                    alt={post.title}
                                    style={{
                                      width: '50px',
                                      height: '50px',
                                      objectFit: 'cover',
                                      borderRadius: '6px',
                                      border: '1px solid #e2e8f0'
                                    }}
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                    }}
                                  />
                                ) : (
                                  <div style={{
                                    width: '50px',
                                    height: '50px',
                                    backgroundColor: '#f7fafc',
                                    borderRadius: '6px',
                                    border: '1px solid #e2e8f0',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: '#a0aec0',
                                    fontSize: '0.75rem'
                                  }}>
                                    No img
                                  </div>
                                )}
                              </div>
                            </td>
                            <td style={{ padding: '16px', verticalAlign: 'middle' }}>
                              <div
                                onClick={() => {
                                  if (post.slug && post.status === 'published') {
                                    history.push(`/ai-notes/${post.slug}`);
                                  }
                                }}
                                style={{
                                  cursor: (post.slug && post.status === 'published') ? 'pointer' : 'default'
                                }}
                              >
                                <div style={{
                                  fontWeight: '500',
                                  color: (post.slug && post.status === 'published') ? '#667eea' : '#2d3748',
                                  marginBottom: '4px',
                                  textDecoration: 'none'
                                }}>
                                  {truncateText(post.title)}
                                </div>
                                <div style={{
                                  fontSize: '0.875rem',
                                  color: '#718096',
                                  lineHeight: '1.4',
                                  whiteSpace: 'pre-line'
                                }}>
                                  {generatePlainTextExcerpt(post.content, 80, 2)}
                                </div>
                              </div>
                            </td>
                            <td style={{ padding: '16px', verticalAlign: 'middle' }}>
                              <Badge
                                bg={post.status === 'published' ? 'success' : post.status === 'draft' ? 'secondary' : post.status === 'scheduled' ? 'info' : 'secondary'}
                                style={{
                                  fontSize: '0.75rem',
                                  padding: '6px 12px',
                                  borderRadius: '6px',
                                  fontWeight: '500'
                                }}
                              >
                                {post.status?.toUpperCase() || 'DRAFT'}
                              </Badge>
                            </td>
                            <td style={{ padding: '16px', verticalAlign: 'middle' }}>
                              {(() => {
                                const seoResult = calculateSeoScore(post);
                                const score = seoResult.score;
                                const color = score >= 90 ? '#2f855a' : score >= 70 ? '#68d391' : score >= 50 ? '#ecc94b' : '#f56565';
                                return (
                                  <div style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '42px',
                                    height: '42px',
                                    borderRadius: '50%',
                                    backgroundColor: `${color}15`,
                                    border: `2px solid ${color}`,
                                    fontWeight: '600',
                                    fontSize: '0.875rem',
                                    color: color
                                  }}>
                                    {score}
                                  </div>
                                );
                              })()}
                            </td>
                            <td style={{ padding: '16px', verticalAlign: 'middle', color: '#4a5568', fontSize: '0.875rem' }}>
                              {post.posted_at ? formatDate(post.posted_at) : '—'}
                            </td>
                            <td style={{ padding: '16px', verticalAlign: 'middle', color: '#4a5568', fontSize: '0.875rem' }}>
                              {post.scheduled_for ? formatDate(post.scheduled_for) : '—'}
                            </td>
                            <td style={{ padding: '16px', verticalAlign: 'middle', color: '#4a5568', fontSize: '0.875rem' }}>
                              {/* Only show updated_at if it exists and differs from posted_at */}
                              {post.updated_at && post.posted_at && 
                               Math.abs(new Date(post.updated_at).getTime() - new Date(post.posted_at).getTime()) > 1000
                                ? formatDate(post.updated_at)
                                : '—'}
                            </td>
                            <td style={{ padding: '16px', verticalAlign: 'middle', textAlign: 'right' }}>
                              <div className="d-flex gap-2 justify-content-end">
                                <Link to={`/admin/ai-notes/edit/${post.id}`}>
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    style={{
                                      borderRadius: '6px',
                                      borderColor: '#667eea',
                                      color: '#667eea',
                                      fontSize: '0.875rem',
                                      padding: '6px 16px'
                                    }}
                                  >
                                    Edit
                                  </Button>
                                </Link>
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => handleDeleteClick(post)}
                                  style={{
                                    borderRadius: '6px',
                                    borderColor: '#f56565',
                                    color: '#f56565',
                                    fontSize: '0.875rem',
                                    padding: '6px 16px'
                                  }}
                                >
                                  Delete
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>

                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div className="d-flex justify-content-between align-items-center p-4" style={{
                        borderTop: '1px solid #e2e8f0',
                        backgroundColor: '#f7fafc'
                      }}>
                        <div style={{ color: '#718096', fontSize: '0.875rem' }}>
                          Showing {startIndex + 1} to {Math.min(endIndex, filteredPosts.length)} of {filteredPosts.length} posts
                        </div>
                        <div className="d-flex gap-2">
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                            disabled={currentPage === 1}
                            style={{
                              borderRadius: '6px',
                              fontSize: '0.875rem'
                            }}
                          >
                            Previous
                          </Button>
                          <div className="d-flex align-items-center gap-2">
                            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => {
                              // Show first page, last page, current page, and pages around current
                              if (
                                page === 1 ||
                                page === totalPages ||
                                (page >= currentPage - 1 && page <= currentPage + 1)
                              ) {
                                return (
                                  <Button
                                    key={page}
                                    variant={page === currentPage ? 'primary' : 'outline-secondary'}
                                    size="sm"
                                    onClick={() => setCurrentPage(page)}
                                    style={{
                                      borderRadius: '6px',
                                      fontSize: '0.875rem',
                                      minWidth: '36px',
                                      backgroundColor: page === currentPage ? '#667eea' : undefined,
                                      borderColor: page === currentPage ? '#667eea' : undefined
                                    }}
                                  >
                                    {page}
                                  </Button>
                                );
                              } else if (
                                page === currentPage - 2 ||
                                page === currentPage + 2
                              ) {
                                return <span key={page} style={{ color: '#718096' }}>...</span>;
                              }
                              return null;
                            })}
                          </div>
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                            disabled={currentPage === totalPages}
                            style={{
                              borderRadius: '6px',
                              fontSize: '0.875rem'
                            }}
                          >
                            Next
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </Card.Body>
            </Card>

            {/* Delete Confirmation Modal - DO NOT extract into separate component, see /.claude/modal-pattern.md */}
            <Modal
              show={showDeleteModal}
              onHide={handleDeleteCancel}
              centered
              style={{ zIndex: 1050 }}
            >
              <Modal.Header
                closeButton
                style={{
                  borderBottom: '1px solid #e2e8f0',
                  padding: '1.5rem'
                }}
              >
                <Modal.Title style={{
                  fontWeight: '500',
                  color: '#2d3748',
                  fontSize: '1.25rem'
                }}>
                  Delete Post
                </Modal.Title>
              </Modal.Header>
              <Modal.Body style={{ padding: '1.5rem' }}>
                <p style={{
                  marginBottom: '1rem',
                  color: '#4a5568',
                  fontSize: '1rem',
                  lineHeight: '1.5'
                }}>
                  Are you sure you want to delete <strong>"{postToDelete?.title}"</strong>?
                </p>
                <p style={{
                  marginBottom: '0',
                  color: '#718096',
                  fontSize: '0.875rem'
                }}>
                  This action cannot be undone.
                </p>
              </Modal.Body>
              <Modal.Footer style={{
                borderTop: '1px solid #e2e8f0',
                padding: '1rem 1.5rem'
              }}>
                <Button
                  variant="outline-secondary"
                  onClick={handleDeleteCancel}
                  disabled={deleting}
                  style={{
                    borderRadius: '8px',
                    padding: '8px 20px',
                    fontWeight: '500'
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="danger"
                  onClick={handleDeleteConfirm}
                  disabled={deleting}
                  style={{
                    borderRadius: '8px',
                    padding: '8px 20px',
                    fontWeight: '500',
                    backgroundColor: '#e53e3e',
                    border: 'none'
                  }}
                >
                  {deleting ? 'Deleting...' : 'Delete Post'}
                </Button>
              </Modal.Footer>
            </Modal>
              </>
            )}

            {/* Tools Tab Content */}
            {activeTab === 'tools' && (
              <>
                <Card style={{
                  border: 'none',
                  borderRadius: '12px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                  marginBottom: '2rem'
                }}>
                  <Card.Body style={{ padding: '2rem' }}>
                    <h4 style={{
                      fontWeight: '500',
                      color: '#2d3748',
                      marginBottom: '0.5rem'
                    }}>
                      Social Media Optimization Tools
                    </h4>
                    <p style={{
                      color: '#718096',
                      fontSize: '0.95rem',
                      marginBottom: '1.5rem'
                    }}>
                      Test how your posts appear when shared on social media platforms.
                    </p>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                      {SOCIAL_TOOLS.map((tool, index) => (
                        <div
                          key={index}
                          style={{
                            padding: '1rem 1.25rem',
                            backgroundColor: '#f7fafc',
                            borderRadius: '8px',
                            border: '1px solid #e2e8f0'
                          }}
                        >
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            gap: '1rem'
                          }}>
                            <div>
                              <h5 style={{
                                fontWeight: '500',
                                color: '#2d3748',
                                marginBottom: '0.25rem',
                                fontSize: '1rem'
                              }}>
                                {tool.name}
                              </h5>
                              <p style={{
                                color: '#718096',
                                fontSize: '0.875rem',
                                marginBottom: 0
                              }}>
                                {tool.description}
                              </p>
                            </div>
                            <a
                              href={tool.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                flexShrink: 0
                              }}
                            >
                              <Button
                                variant="outline-primary"
                                size="sm"
                                style={{
                                  borderRadius: '6px',
                                  borderColor: '#667eea',
                                  color: '#667eea',
                                  fontSize: '0.875rem',
                                  padding: '6px 16px',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                Open Tool
                              </Button>
                            </a>
                          </div>
                        </div>
                      ))}
                    </div>
                  </Card.Body>
                </Card>

                {/* Quick Test Section */}
                <Card style={{
                  border: 'none',
                  borderRadius: '12px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)'
                }}>
                  <Card.Body style={{ padding: '2rem' }}>
                    <h4 style={{
                      fontWeight: '500',
                      color: '#2d3748',
                      marginBottom: '0.5rem'
                    }}>
                      Quick Test URLs
                    </h4>
                    <p style={{
                      color: '#718096',
                      fontSize: '0.95rem',
                      marginBottom: '1rem'
                    }}>
                      Copy a post URL and paste it into the tools above to test social sharing previews.
                    </p>
                    <div style={{
                      backgroundColor: 'rgba(102, 126, 234, 0.08)',
                      borderRadius: '8px',
                      padding: '1rem',
                      fontSize: '0.875rem',
                      color: '#4a5568'
                    }}>
                      <strong>Tip:</strong> After making changes to meta tags, use Facebook's debugger to "Scrape Again"
                      and force a refresh of cached previews.
                    </div>
                  </Card.Body>
                </Card>
              </>
            )}
          </Col>
        </Row>
      </Container>
    </div>
  );
};

function mapStateToProps({ auth }) {
  return { auth };
}

export default connect(mapStateToProps)(AiNotesAdmin);

